import { useCallback } from 'react';
import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult
} from '@tanstack/react-query';
import { ErrorCode } from '@bliks-io/contracts';
import { processDraftKeys, processKeys, processLayoutKeys } from '../../query-keys';

/**
 * `409 DRAFT_EDGE_NOT_FOUND` means the source→target successor edge a draft
 * mutation wanted to consume no longer exists — typically a double-fired
 * request whose first run already consumed it. Callers treat it as a soft
 * no-op (no toast, no error surface) and refetch the draft caches themselves:
 * a `runDraftTransaction` failure restores its snapshot without refetching.
 */
export function isDraftEdgeNotFound(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { statusCode?: unknown; message?: unknown };
  return e.statusCode === 409 && e.message === ErrorCode.DRAFT_EDGE_NOT_FOUND;
}

/**
 * Invalidate every cache a draft mutation can touch: the draft detail (all
 * three node arrays live there), the draft layout, and the process list, which
 * shows each process's last draft edit. Refetches reconcile the cache with
 * server truth no matter what the mutation's own handlers wrote — or failed to
 * write. The list is only marked stale, so it refetches when the user is back
 * on it rather than on every edit in the editor.
 */
export async function invalidateDraftCaches(
  queryClient: QueryClient,
  processId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: processDraftKeys.detail(processId)
    }),
    queryClient.invalidateQueries({
      queryKey: processLayoutKeys.detail(processId, 'draft')
    }),
    queryClient.invalidateQueries({ queryKey: processKeys.list(), refetchType: 'none' })
  ]);
}

/**
 * Run one editor gesture as one registered draft mutation, pending from its
 * first request to its last, so Publish's flush and "Saving…" wait for all of
 * it — including raw persist calls and the gaps between chained mutations.
 * On success the inner writes have already refetched; a failure refetches here.
 * `networkMode: 'always'`: the gesture sends nothing itself, so pausing it
 * offline would only defer its optimistic stage and queue repeated clicks.
 */
export function useDraftGesture<TArgs extends unknown[], TResult>(
  processId: string,
  run: (...args: TArgs) => Promise<TResult>
): (...args: TArgs) => Promise<TResult> {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationKey: processDraftKeys.mutations(processId),
    mutationFn: (args: TArgs) => run(...args),
    networkMode: 'always',
    onError: () => invalidateDraftCaches(queryClient, processId)
  });
  return useCallback((...args: TArgs) => mutateAsync(args), [mutateAsync]);
}

/**
 * `useMutation` wrapper for draft mutations: ALWAYS invalidates the draft
 * detail + draft layout caches on settle (success and error alike), after
 * chaining the caller's own `onSettled`. This is the structural backstop —
 * targeted `onSuccess` cache writes keep the UI instant, and the settle
 * refetch guarantees convergence even when a write targets the wrong array,
 * misses a cascade, or the optimistic restore races a later edit.
 *
 * Not for draft lifecycle mutations (create/publish/discard) — those REMOVE
 * the draft keys; invalidating here would refetch a dead draft.
 */
export function useDraftMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown
>(
  processId: string,
  options: UseMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData, TError, TVariables, TContext> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: processDraftKeys.mutations(processId),
    ...options,
    onSettled: async (...args) => {
      try {
        await options.onSettled?.(...args);
      } finally {
        await invalidateDraftCaches(queryClient, processId);
      }
    }
  });
}
